import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    addDays,
    differenceInCalendarDays,
    differenceInHours,
    differenceInMinutes,
    eachMinuteOfInterval,
    endOfDay,
    format,
    isSameDay,
    roundToNearestMinutes,
    setHours,
    startOfDay,
    startOfHour,
    subDays,
} from "date-fns";
import { Box, Button, Text } from "grommet";
import { orderBy, toInteger } from "lodash";
import * as React from "react";
import { Cell, Grid } from "styled-css-grid";
import ScrollContainer from "../components/ScrollContainer";
import { CalendarViewEvent } from "./CalendarView";
import RenderAllDayEvents from "./RenderAllDayEvents";

interface DayViewProps {
    day: any;
    events: CalendarViewEvent[];
    showTimes?: boolean;
}

const DayView = ({ day, events, showTimes = true }: DayViewProps) => {
    const minuteSeparation = 15;

    const [currentDay, setCurrentDay] = React.useState(new Date());
    const [eventsToday, setEventsToday] = React.useState([] as CalendarViewEvent[]);
    const [gridColumns, setGridColumns] = React.useState(["auto"] as string[]);

    const [now, setNow] = React.useState(new Date());

    const [dayHours, setDayHours] = React.useState(
        eachMinuteOfInterval(
            {
                start: setHours(startOfHour(currentDay), 0),
                end: setHours(startOfHour(currentDay), 24),
            },
            { step: minuteSeparation }
        )
    );
    const nowRef = React.useRef<HTMLElement>(null);

    React.useEffect(() => {
        if (nowRef.current) {
            nowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [nowRef]);

    React.useEffect(() => {
        getCurrentDay();
    }, [day]);

    React.useEffect(() => {
        getDayEvents();

        setDayHours(
            eachMinuteOfInterval(
                {
                    start: setHours(startOfHour(currentDay), 0),
                    end: setHours(startOfHour(currentDay), 24),
                },
                { step: minuteSeparation }
            )
        );
    }, [currentDay]);

    React.useEffect(() => {
        getGridColumns();
    }, [eventsToday]);

    const getCurrentDay = () => {
        if (!isNaN(day)) {
            setCurrentDay(new Date(day));
        }
    };

    const getDayEvents = () => {
        let newArray = [] as CalendarViewEvent[];
        let tempEvents = orderBy(events, "start") as CalendarViewEvent[];

        if (tempEvents.length > 0) {
            tempEvents.forEach((event, i) => {
                let days = differenceInCalendarDays(event.end, event.start);
                for (let i = 0; i <= days; i++) {
                    let tempEvent = { ...event };
                    tempEvent.start = addDays(tempEvent.start, i);
                    tempEvent.dayOfEvent = i;

                    tempEvent.duration = (
                        differenceInMinutes(roundToNearestMinutes(tempEvent.end, { nearestTo: minuteSeparation }), roundToNearestMinutes(tempEvent.start, { nearestTo: minuteSeparation })) /
                        minuteSeparation
                    ).toString();

                    tempEvent.position = getEventPos(tempEvent);

                    if (tempEvent.start >= startOfDay(currentDay) && tempEvent.start <= endOfDay(currentDay)) {
                        newArray = [...newArray, tempEvent];
                    }
                }
            });

            newArray.forEach((event, i) => {
                event.position.left += newArray.filter((e) => e.position.top == event.position.top && e.position.left == event.position.left).length;
            });

            setEventsToday(newArray.filter((event) => !event.allDay));
        }
    };

    const previousDay = () => {
        setCurrentDay(subDays(currentDay, 1));
    };

    const nextDay = () => {
        setCurrentDay(addDays(currentDay, 1));
    };

    const getGridColumns = () => {
        if (eventsToday.length == 0) {
            setGridColumns(["auto"]);
        } else {
            let columnWidth = 95 / eventsToday.length;
            setGridColumns(eventsToday.map(() => columnWidth.toString() + "%"));
        }
    };

    const getHeight = (date: Date) => {
        let offsetHours = toInteger(format(date, "k")) * 4;
        let offsetMinutes = (toInteger(format(roundToNearestMinutes(date, { nearestTo: minuteSeparation, roundingMethod: "floor" }), "m")) / 60) * 4;
        let t = offsetHours + offsetMinutes + 1;
        return t;
    };

    const getEventPos = (event) => {
        if (isSameDay(event.start, currentDay)) {
            let halfHours = eachMinuteOfInterval(
                {
                    start: startOfHour(event.start),
                    end: setHours(startOfHour(currentDay), 24),
                },
                { step: minuteSeparation }
            ).length;

            if (event.days > 0) {
                if (event.dayOfEvent == event.days) {
                    return { height: differenceInHours(event.end, startOfDay(currentDay)) * 2 + 1, top: 1 };
                } else if (event.dayOfEvent == 0) {
                    return { height: halfHours, top: (toInteger(format(event.start, "k")) + toInteger(format(roundToNearestMinutes(event.start, { nearestTo: minuteSeparation }), "m")) / 60) * 2 + 1 };
                } else {
                    return { height: 49, top: 1 };
                }
            } else {
                let h = toInteger(event.duration);

                let offsetHours = toInteger(format(event.start, "k")) * 4;
                let offsetMinutes = (toInteger(format(roundToNearestMinutes(event.start, { nearestTo: minuteSeparation, roundingMethod: "floor" }), "m")) / 60) * 4;
                let t = offsetHours + offsetMinutes + 1;
                return {
                    height: h,
                    top: t,
                    left: 0,
                };
            }
        } else {
            return { height: 0, top: 0, left: 0 };
        }
    };

    const getEventStartEnd = (event) => {
        if (isSameDay(event.start, currentDay)) {
            let currentEvent;
            events.forEach((e) => {
                if (e.id == event.id) {
                    currentEvent = e;
                }
            });

            if (event.days > 0) {
                if (event.dayOfEvent == event.days) {
                    return { start: `on ` + format(currentEvent.start, "do MMMM"), end: "Today" };
                } else if (event.dayOfEvent == 0) {
                    return { start: "Today", end: `on ` + format(currentEvent.end, "do MMMM") };
                } else {
                    return { start: `on ` + format(currentEvent.start, "do MMMM"), end: `on ` + format(currentEvent.end, "do MMMM") };
                }
            } else {
                return { start: "", end: "" };
            }
        }
    };

    return (
        <Box gap="xsmall" fill>
            <Box gap="small">
                <RenderAllDayEvents day={currentDay} events={events}></RenderAllDayEvents>
                <Box fill>
                    <Grid flow="column" columns={showTimes ? ["max(5%,75px)", ...gridColumns].join(" ") : ["1px", ...gridColumns].join(" ")}>
                        {dayHours.map((hour, key) => (
                            <Cell key={key} top={key + 1} height={1}>
                                <Text>{showTimes && key % 4 == 0 ? format(hour, "HH:mm") : <>&nbsp;</>}</Text>
                            </Cell>
                        ))}

                        {/* 48 rows per half hour for the day. Event start is formatted to the hour */}
                        {eventsToday.map((event, key) => (
                            <Cell key={event.id} height={event.position?.height} left={1 + event.position.left} top={event.position?.top}>
                                <Box fill background={{ color: event.color, opacity: "medium" }} border={[{ size: "medium", color: event.color, side: "left" }]} pad="small" round="xsmall">
                                    <Text weight="bold">{event.title}</Text>
                                    <Text>{event.body}</Text>
                                    <Text>{`${format(event.start, "hh:mm a")} ${getEventStartEnd(event)?.start} - ${format(event.end, "hh:mm a")} ${getEventStartEnd(event)?.end}`}</Text>
                                </Box>
                            </Cell>
                        ))}

                        <Cell ref={nowRef} height={1} left={2} top={getHeight(now)} style={{ position: "relative", maxWidth: "100%" }}>
                            <Box background={{ color: "brand", opacity: "medium" }} height={"2px"} style={{ maxWidth: "110%", position: "absolute", width: "110%" }}>
                                &nbsp;
                            </Box>
                        </Cell>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};

export default DayView;
